import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "../../../pages/helpCenter";
import { BiCalendar } from "react-icons/bi";
import { Modal } from "./modal";
import { RichTextEditor } from "../richTextEditor";

export const ArticleModal = ({ open, initial, categories, subcategories, defaultCategoryId, defaultSubcategoryId, onClose, onSave }) => {
  const { register, control, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      title: "",
      description: "",
      content: "<p></p>",
      categoryId: "",
      subcategoryId: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        title: initial?.title || "",
        description: initial?.description || "",
        content: initial?.content || "<p></p>",
        categoryId: initial?.categoryId || defaultCategoryId || categories[0]?.id || "",
        subcategoryId: initial?.subcategoryId || defaultSubcategoryId || "",
      });
    }
  }, [open, initial, defaultCategoryId, defaultSubcategoryId, categories, reset]);

  const title = watch("title");
  const categoryId = watch("categoryId");
  const subcategoryId = watch("subcategoryId");
  const canSave = title.trim().length > 0 && categoryId;

  const subcategoryOptions = subcategories.filter((s) => s.categoryId === categoryId);

  // When the category changes, drop the selected subcategory if it no
  // longer belongs to the new category (mirrors the old handleCategoryChange).
  const handleCategoryChange = (e) => {
    const newCatId = e.target.value;
    setValue("categoryId", newCatId);
    if (!subcategories.some((s) => s.id === subcategoryId && s.categoryId === newCatId)) {
      setValue("subcategoryId", "");
    }
  };

  const onSubmit = (data) => {
    onSave({
      title: data.title.trim(),
      description: data.description.trim(),
      content: data.content,
      categoryId: data.categoryId,
      subcategoryId: data.subcategoryId || null,
    });

    // Only clear when creating a new article
    if (!initial) {
      reset({
        title: "",
        description: "",
        content: "<p></p>",
        categoryId: defaultCategoryId || categories[0]?.id || "",
        subcategoryId: "",
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="max-w-2xl"
      title={initial ? "Edit article" : "New article"}
      subtitle={initial ? `Created ${new Date(initial.createdAt).toLocaleDateString()}` : "Created date is set automatically to today."}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={!canSave} onClick={handleSubmit(onSubmit)}>
            {initial ? "Save changes" : "Publish article"}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Title <span className="text-rose-500">*</span>
          </span>
          <input
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
            autoFocus
            placeholder="e.g. Creating your workspace"
            {...register("title")}
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Description
          </span>
          <textarea
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600 resize-none"
            rows={2}
            placeholder="One or two sentence summary"
            {...register("description")}
          />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Category <span className="text-rose-500">*</span>
            </span>
            <input type="hidden" {...register("categoryId")} />
            <select
              className="w-full rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-500 cursor-not-allowed"
              value={categoryId}
              disabled
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Subcategory
            </span>
            <input type="hidden" {...register("subcategoryId")} />
            <select
              className="w-full rounded-lg border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-500 cursor-not-allowed"
              value={subcategoryId}
              disabled
            >
              <option value="">None</option>
              {subcategoryOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-slate-400">Optional</span>
          </label>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Content
          </span>
          <Controller
            name="content"
            control={control}
            render={({ field }) => <RichTextEditor value={field.value} onChange={field.onChange} />}
          />
        </label>

        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <BiCalendar size={13} />
          {initial ? `Created ${new Date(initial.createdAt).toLocaleDateString()}` : `Will be created ${new Date().toLocaleDateString()}`}
        </div>
      </form>
    </Modal>
  );
};